/**
 * Shared MYM game UI history.
 *
 * Parent (/mindyourmusic) and same-origin game iframes are separate JS realms,
 * so the listener store lives on window.top. History entries keep the URL
 * unchanged and put navigation in history.state.mymGameNav.
 *
 * Do not pushState from popstate handlers except to re-arm the leave-site trap.
 */

export const MYM_NAV_KEY = "mymGameNav";
export const MYM_GUARD_KEY = "backGuard";

const getHistoryWindow = () => {
  if (typeof window === "undefined") return null;
  try {
    if (window.top && window.top !== window) {
      void window.top.location.href;
      return window.top;
    }
  } catch {
    // Cross-origin iframe: only this window's history is available.
  }
  return window;
};

const store = () => {
  const w = getHistoryWindow();
  if (!w) return { listeners: new Set(), attached: false, lastNav: null };
  if (!w.__mymGameHistoryStore) {
    w.__mymGameHistoryStore = {
      listeners: new Set(),
      attached: false,
      lastNav: null,
    };
  }
  return w.__mymGameHistoryStore;
};

export const getGameNav = (state) => {
  const w = getHistoryWindow();
  const raw = state !== undefined ? state : w?.history?.state;
  return raw?.[MYM_NAV_KEY] || null;
};

const signature = (nav) => {
  if (!nav) return "";
  return `${nav.game}|${nav.screen}|${JSON.stringify(nav.data ?? null)}|${!!nav.forceLocked}`;
};

const emit = (nav, reason) => {
  const s = store();
  s.lastNav = nav;
  s.listeners.forEach((fn) => {
    try {
      fn(nav, reason);
    } catch (err) {
      console.error("[mymGameHistory] listener failed", err);
    }
  });
};

const attachPopListener = () => {
  const w = getHistoryWindow();
  const s = store();
  if (!w || s.attached) return;
  s.attached = true;
  w.addEventListener("popstate", (event) => {
    emit(getGameNav(event.state), "pop");
  });
};

export const subscribeGameNav = (fn) => {
  attachPopListener();
  const s = store();
  s.listeners.add(fn);
  return () => s.listeners.delete(fn);
};

const writeState = (nav, method) => {
  const w = getHistoryWindow();
  if (!w) return;
  const prev = { ...(w.history.state || {}) };
  if (nav) {
    prev[MYM_NAV_KEY] = nav;
    delete prev[MYM_GUARD_KEY];
  } else {
    delete prev[MYM_NAV_KEY];
  }
  w.history[method](prev, "", w.location.href);
};

/**
 * Open (or tag) the first game history entry.
 * If the current entry is the leave-site trap, pushState.
 * If it looks like an iframe document load (no nav, no trap), replaceState
 * so the iframe does not create an extra blank Back step.
 */
export const openGame = ({
  game,
  screen = "home",
  data,
  forceLocked = false,
} = {}) => {
  const w = getHistoryWindow();
  if (!w || !game) return;

  const current = getGameNav();
  const next = {
    modal: "game",
    game,
    screen,
    data,
    forceLocked: !!forceLocked,
    depth: 1,
  };

  if (current?.game === game && current.depth >= 1) {
    return;
  }

  if (current && current.game !== game) {
    writeState({ ...next, depth: 1 }, "replaceState");
    emit(next, "replace");
    return;
  }

  const raw = w.history.state || {};
  if (raw[MYM_GUARD_KEY] && !raw[MYM_NAV_KEY]) {
    writeState(next, "pushState");
    emit(next, "push");
    return;
  }

  if (!raw[MYM_NAV_KEY]) {
    writeState(next, "replaceState");
    emit(next, "replace");
    return;
  }

  writeState(next, "pushState");
  emit(next, "push");
};

export const ensureGameOpen = (opts) => {
  const current = getGameNav();
  if (current?.game === opts?.game) return;
  openGame(opts);
};

export const goTo = ({ game, screen, data, forceLocked } = {}) => {
  const w = getHistoryWindow();
  if (!w || !screen) return;

  const current = getGameNav() || {};
  const next = {
    modal: "game",
    game: game || current.game,
    screen,
    data,
    forceLocked: forceLocked ?? !!current.forceLocked,
    depth: (current.depth || 0) + 1,
  };
  if (!next.game) return;
  if (signature(current) === signature(next)) return;

  writeState(next, "pushState");
  emit(next, "push");
};

export const goBack = () => {
  getHistoryWindow()?.history.back();
};

export const goForward = () => {
  getHistoryWindow()?.history.forward();
};

/** Pop all game UI entries so the current entry is the leave-site trap / MYM. */
export const closeGame = () => {
  const w = getHistoryWindow();
  if (!w) return;
  const nav = getGameNav();
  if (!nav) {
    emit(null, "close");
    return;
  }
  if (nav.forceLocked) return;
  const depth = Math.max(1, nav.depth || 1);
  w.history.go(-depth);
};

export const clearStaleGameNav = () => {
  const w = getHistoryWindow();
  if (!w) return;
  const nav = getGameNav();
  if (!nav) return;
  const prev = { ...(w.history.state || {}) };
  delete prev[MYM_NAV_KEY];
  prev[MYM_GUARD_KEY] = true;
  w.history.replaceState(prev, "", w.location.href);
};

export const isLeaveTrapState = (state) => {
  const raw = state !== undefined ? state : getHistoryWindow()?.history?.state;
  return !!(raw && raw[MYM_GUARD_KEY] && !raw[MYM_NAV_KEY]);
};
